import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Wrench, CheckCircle2, XCircle, Loader2, AlertCircle } from 'lucide-react';
import { Card, CardContent, Button } from '@scio3/ui';
import { getWriteToolDisplayName, formatToolArgs } from '../utils/tool-display';

interface ApprovalCardProps {
  toolCallId: string;
  toolName: string;
  args: Record<string, unknown>;
  onApprove: (toolCallId: string) => void;
  onDeny: (toolCallId: string) => void;
  /** When set, renders the card as a read-only receipt instead of showing action buttons. */
  decided?: 'approved' | 'denied' | 'executing' | 'failed';
}

/**
 * Inline approval card rendered when a write tool needs user confirmation.
 * Shows the tool action name, a brief summary of arguments, and
 * Approve / Deny buttons that send the result back via addToolResult.
 * After the user responds, `decided` is passed by the parent so the card
 * stays visible as a permanent receipt of what was approved or denied.
 */
export function ApprovalCard({
  toolCallId,
  toolName,
  args,
  onApprove,
  onDeny,
  decided,
}: ApprovalCardProps) {
  const { t } = useTranslation();
  const [responded, setResponded] = useState(false);

  const displayName = t(getWriteToolDisplayName(toolName));
  const argsSummary = formatToolArgs(toolName, args, t);

  return (
    <Card className="my-2 border-primary/20">
      <CardContent className="p-3">
        <div className="mb-2 flex items-center gap-2 text-sm font-medium">
          <Wrench className="h-4 w-4 text-primary" />
          <span>{displayName}</span>
        </div>

        {argsSummary && (
          <p className="mb-3 whitespace-pre-line text-xs text-muted-foreground">{argsSummary}</p>
        )}

        {decided ? (
          <div className="flex items-center gap-1.5 text-xs">
            {decided === 'executing' ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                <span className="text-muted-foreground">{t('optibot.actionPending')}</span>
              </>
            ) : decided === 'failed' ? (
              <>
                <AlertCircle className="h-3.5 w-3.5 shrink-0 text-destructive" />
                <span className="text-destructive">{t('optibot.actionFailed')}</span>
              </>
            ) : decided === 'approved' ? (
              <>
                <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                <span className="text-green-600">{t('optibot.actionCompleted')}</span>
              </>
            ) : (
              <>
                <XCircle className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-muted-foreground">{t('optibot.actionDenied')}</span>
              </>
            )}
          </div>
        ) : (
          <>
            <p className="mb-3 text-xs text-muted-foreground">{t('optibot.actionPending')}</p>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="default"
                disabled={responded}
                onClick={() => {
                  setResponded(true);
                  onApprove(toolCallId);
                }}
              >
                {t('optibot.approve')}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={responded}
                onClick={() => {
                  setResponded(true);
                  onDeny(toolCallId);
                }}
              >
                {t('optibot.deny')}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
